import { useRef } from "react";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useExamUpload } from "@/hooks/use-exams";
import { BrowserFile } from "@/lib/browser-file";
import { ExamLabels } from "@/lib/labels/exams";
import { cn } from "@/lib/utils";

const ALLOWED_MIME = ["application/pdf", "image/jpeg", "image/png", "image/webp"] as const;

const ACCEPT = ALLOWED_MIME.join(",");

function allowedMime(value: string) {
	return ALLOWED_MIME.find((mime) => mime === value);
}

type ExamUploadTarget = { patientId: number; personId?: number } | { personId: number };

export function RegisterExamUpload(target: ExamUploadTarget) {
	const inputRef = useRef<HTMLInputElement>(null);
	const upload = useExamUpload();

	const onPick = async (file: File) => {
		const mime = allowedMime(file.type);

		if (mime === undefined) {
			toast.error(ExamLabels.upload.unsupportedFormat, {
				position: "bottom-left",
			});

			return;
		}

		const data = await BrowserFile.toBase64(file);
		const patientId = "patientId" in target ? target.patientId : null;
		const personId = target.personId === undefined ? null : target.personId;

		upload.mutate(
			{ patient_id: patientId, person_id: personId, mime, data },
			{
				onSuccess: () =>
					toast.success(ExamLabels.upload.registered, {
						position: "bottom-left",
					}),
			},
		);
	};

	return (
		<span className="inline-flex">
			<input
				ref={inputRef}
				type="file"
				accept={ACCEPT}
				className="hidden"
				onChange={(e) => {
					const file = e.target.files?.[0];

					if (file) {
						void onPick(file);
					}

					e.target.value = "";
				}}
			/>

			<button
				type="button"
				data-slot="register-exam-upload"
				onClick={() => inputRef.current?.click()}
				disabled={upload.isPending}
				className={cn(
					"inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 font-mono text-xs font-medium leading-none text-muted-foreground transition-colors",
					"hover:bg-border/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
					"disabled:cursor-not-allowed disabled:opacity-60",
				)}
			>
				{upload.isPending && <Loader2 className="size-3 animate-spin" />}
				{!upload.isPending && <FileUp className="size-3" />}
				{upload.isPending && "registrando exame..."}
				{!upload.isPending && ExamLabels.upload.registerAction}
			</button>
		</span>
	);
}
